import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

// 🔹 Reusable Try It Button
function TryItButton({ code }) {
  const handleClick = () => {
    const encodedCode = encodeURIComponent(code);
    window.open(`/editorJS?code=${encodedCode}`, "_blank");
  };

  return (
    <button onClick={handleClick} className="try_it_button">
      ▶ Try It
    </button>
  );
}

export default function JS_Logical_Operators() {
  return (
    <div className="tutorial-container">
      {/* Title */}
      <h1 className="tutorial-title">🤔 JavaScript Logical Operators</h1>

      {/* Intro */}
      <p className="tutorial-intro">
        Logical operators are used to combine multiple conditions or perform
        logical operations on boolean values (<code>true</code> /{" "}
        <code>false</code>). These operators are widely used in decision-making
        statements like <code>if</code>, <code>while</code>, and{" "}
        <code>for</code> loops.
      </p>

      {/* Key Points Table */}
      <div
        className="Key_point_container"
        style={{
          background: "linear-gradient(135deg, #f0f9ff, #e0f2fe)",
        }}
      >
        <h2 style={{ color: "#075985", marginBottom: "1rem" }}>
          🔑 Key Points
        </h2>
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            fontSize: "1.1rem",
            backgroundColor: "white",
            borderRadius: "8px",
            overflow: "hidden",
            boxShadow: "0 0 10px rgba(0,0,0,0.1)",
          }}
        >
          <thead style={{ backgroundColor: "#e2e8f0" }}>
            <tr>
              <th style={{ padding: "12px", textAlign: "left" }}>Operator</th>
              <th style={{ padding: "12px", textAlign: "left" }}>
                Description
              </th>
              <th style={{ padding: "12px", textAlign: "left" }}>Example</th>
              <th style={{ padding: "12px", textAlign: "left" }}>Result</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={{ padding: "10px" }}>&&</td>
              <td style={{ padding: "10px" }}>Logical AND</td>
              <td style={{ padding: "10px" }}>true && false</td>
              <td style={{ padding: "10px" }}>false</td>
            </tr>
            <tr style={{ backgroundColor: "#f9fafb" }}>
              <td style={{ padding: "10px" }}>||</td>
              <td style={{ padding: "10px" }}>Logical OR</td>
              <td style={{ padding: "10px" }}>true || false</td>
              <td style={{ padding: "10px" }}>true</td>
            </tr>
            <tr>
              <td style={{ padding: "10px" }}>!</td>
              <td style={{ padding: "10px" }}>Logical NOT</td>
              <td style={{ padding: "10px" }}>!true</td>
              <td style={{ padding: "10px" }}>false</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Example 1: Logical AND (&&) */}
      <div className="tutorial-section">
        <h2 className="section-title">1️⃣ Logical AND (&&)</h2>
        <p>
          The <code>&&</code> operator returns <code>true</code> only if both
          operands are <code>true</code>.
        </p>

        <div className="code-demo-box">
          <CodeCopyBlock
            code={`let a = 10;
let b = 20;
console.log(a > 5 && b > 15); // true
console.log(a > 15 && b > 10); // false`}
          />
          <TryItButton
            code={`let a = 10;
let b = 20;
console.log(a > 5 && b > 15); // true
console.log(a > 15 && b > 10); // false`}
          />
        </div>
      </div>

      {/* Example 2: Logical OR (||) */}
      <div className="tutorial-section">
        <h2 className="section-title">2️⃣ Logical OR (||)</h2>
        <p>
          The <code>||</code> operator returns <code>true</code> if at least one
          condition is <code>true</code>.
        </p>

        <div className="code-demo-box">
          <CodeCopyBlock
            code={`let x = 5;
let y = 8;
console.log(x > 10 || y > 5); // true
console.log(x > 10 || y < 5); // false`}
          />
          <TryItButton
            code={`let x = 5;
let y = 8;
console.log(x > 10 || y > 5); // true
console.log(x > 10 || y < 5); // false`}
          />
        </div>
      </div>

      {/* Example 3: Logical NOT (!) */}
      <div className="tutorial-section">
        <h2 className="section-title">3️⃣ Logical NOT (!)</h2>
        <p>
          The <code>!</code> operator reverses the boolean value of its operand.
        </p>

        <div className="code-demo-box">
          <CodeCopyBlock
            code={`let isLoggedIn = false;
console.log(!isLoggedIn); // true`}
          />
          <TryItButton
            code={`let isLoggedIn = false;
console.log(!isLoggedIn); // true`}
          />
        </div>
      </div>

      {/* Example 4: Combined Example */}
      <div className="tutorial-section">
        <h2 className="section-title">4️⃣ Combined Example</h2>
        <p>
          You can combine multiple logical operators to create complex
          conditions in your code.
        </p>

        <div className="code-demo-box">
          <CodeCopyBlock
            code={`let age = 25;
let hasLicense = true;

if (age >= 18 && hasLicense) {
  console.log("You can drive!");
} else {
  console.log("You cannot drive yet.");
}`}
          />
          <TryItButton
            code={`let age = 25;
let hasLicense = true;

if (age >= 18 && hasLicense) {
  console.log("You can drive!");
} else {
  console.log("You cannot drive yet.");
}`}
          />
        </div>
      </div>

      {/* Next Steps */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef9c3, #fde68a)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginTop: "2rem",
        }}
      >
        <h2 style={{ color: "#92400e" }}>🚀 Next Steps</h2>
        <ul className="tutorial-list-points">
          <li>
            Learn about the <b>Ternary Operator</b> for concise conditional
            expressions.
          </li>
          <li>
            Explore <b>Conditional Statements</b> like <code>if</code>,{" "}
            <code>else if</code>, and <code>switch</code>.
          </li>
          <li>
            Practice building decision-based programs using logical operators.
          </li>
        </ul>
      </div>
    </div>
  );
}
